/**
 * SeoBlock — SEO text block with H2 heading, paragraphs, and FAQ accordion.
 * Placed above the footer on each page for keyword-rich content.
 * Accessible: uses <details>/<summary> for FAQ items (no JS required).
 */
import { useState } from "react";

interface FaqItem {
  q: string;
  a: string;
}

interface SeoBlockProps {
  heading: string;
  paragraphs: string[];
  faq?: FaqItem[];
}

export default function SeoBlock({ heading, paragraphs, faq }: SeoBlockProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      aria-label={heading}
      style={{
        background: "oklch(0.97 0.015 85)",
        borderTop: "1px solid oklch(0.90 0.03 85)",
      }}
    >
      <div className="container py-12 md:py-16">
        {/* H2 heading — primary SEO signal */}
        <h2
          className="text-2xl md:text-3xl font-bold mb-6"
          style={{
            fontFamily: "'Outfit', sans-serif",
            color: "oklch(0.22 0.06 155)",
          }}
        >
          {heading}
        </h2>

        {/* Body paragraphs */}
        <div className="space-y-4 mb-8 max-w-3xl">
          {paragraphs.map((para, i) => (
            <p
              key={i}
              className="text-base leading-relaxed"
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: "oklch(0.35 0.04 155)",
              }}
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
        </div>

        {/* FAQ accordion */}
        {faq && faq.length > 0 && (
          <div className="max-w-3xl">
            <h3
              className="text-lg font-semibold mb-4"
              style={{
                fontFamily: "'Outfit', sans-serif",
                color: "oklch(0.28 0.06 155)",
              }}
            >
              Najczęstsze pytania
            </h3>
            <div className="space-y-2" itemScope itemType="https://schema.org/FAQPage">
              {faq.map((item, i) => {
                const isOpen = openIndex === i;
                return (
                  <div
                    key={i}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                    style={{
                      border: "1px solid oklch(0.88 0.04 85)",
                      borderRadius: "8px",
                      overflow: "hidden",
                      background: "oklch(1 0 0)",
                    }}
                  >
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => setOpenIndex(isOpen ? null : i)}
                      className="w-full text-left flex items-center justify-between gap-3 px-5 py-4 transition-colors"
                      style={{
                        fontFamily: "'Outfit', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        color: "oklch(0.28 0.06 155)",
                        background: isOpen ? "oklch(0.95 0.025 85)" : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <span itemProp="name">{item.q}</span>
                      <span
                        aria-hidden="true"
                        style={{
                          flexShrink: 0,
                          transition: "transform 200ms cubic-bezier(0.23,1,0.32,1)",
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          color: "oklch(0.50 0.18 45)",
                        }}
                      >
                        ▾
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                        className="px-5 pb-4 pt-1"
                        style={{
                          borderTop: "1px solid oklch(0.92 0.03 85)",
                        }}
                      >
                        <p
                          itemProp="text"
                          className="text-sm leading-relaxed"
                          style={{
                            fontFamily: "'Outfit', sans-serif",
                            color: "oklch(0.40 0.04 155)",
                          }}
                        >
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
